import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";

import {
  getCars,
  createCar,
  updateCar,
  deleteCar as deleteCarApi,
  updateCarMileage,
} from "../services/api/carApi";

import { getAuthData } from "../services/api/authApi";
import { extractErrorMessage } from "../services/api/errorHandler";
import carImage from "../assets/Image/car-image.webp";

// =========================
// LocalStorage Key
// =========================

const SELECTED_CAR_KEY = "selectedCarId";

const CarContext = createContext(null);

// =========================
// تبدیل Backend به Frontend
// =========================

const transformBackendCar = (backendCar) => ({
  id: backendCar.id,

  // تصویر ماشین
  Src: carImage,

  // کیلومتر برای نمایش
  Km:
    backendCar.currentMileage !== undefined &&
    backendCar.currentMileage !== null
      ? Number(backendCar.currentMileage).toLocaleString("fa-IR")
      : "۰",

  CarModel:
    backendCar.title ||
    backendCar.brand ||
    "نامشخص",

  ProductionYear:
    backendCar.buildYear?.toString() || "نامشخص",

  CarColor:
    backendCar.color || "نامشخص",

  // پلاک
  Plate1: backendCar.plate1 || "",
  Plate2: backendCar.plate2 || "",
  Plate3: backendCar.plate3 || "",
  End: backendCar.plateEnd || "",

  // اطلاعات تکمیلی
  chassisNumber:
    backendCar.chassisNumber || "",

  engineModel:
    backendCar.engineModel || "",

  // =========================
  // اطلاعات خام Backend
  // =========================

  title:
    backendCar.title || "",

  brand:
    backendCar.brand || "",

  currentMileage:
    backendCar.currentMileage ?? 0,

  buildYear:
    backendCar.buildYear ?? "",

  color:
    backendCar.color || "",

  plate:
    backendCar.plate || "",

  image: carImage,
});

// =========================
// Provider
// =========================

export function CarProvider({ children }) {
  const [cars, setCars] = useState([]);
  const [selectedCarId, setSelectedCarId] = useState(() => {
    const savedCarId = localStorage.getItem(SELECTED_CAR_KEY);
    return savedCarId || null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCars = useCallback(async () => {
    const { user: authUser } = getAuthData();
    if (!authUser?.id) {
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getCars();

      console.log("GET CARS RESPONSE:", response);

      if (response.success && Array.isArray(response.data)) {
        const transformedCars = response.data.map(transformBackendCar);
        setCars(transformedCars);
        setSelectedCarId((currentSelectedId) => {
          const exists = transformedCars.some((car) => String(car.id) === String(currentSelectedId));
          if (exists) return currentSelectedId;
          if (transformedCars.length) return transformedCars[0].id;
          return null;
        });
        return transformedCars;
      }

      setCars([]);
      setSelectedCarId(null);
      return [];
    } catch (err) {
      console.error("FETCH CARS ERROR:", err);
      setError(extractErrorMessage(err, "خطا در دریافت ماشین‌ها"));
      setCars([]);
      setSelectedCarId(null);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // =========================
  // دریافت ماشین‌ها هنگام Load
  // =========================

  useEffect(() => {
    fetchCars().catch(() => {});
  }, [fetchCars]);

  // =========================
  // ذخیره انتخاب خودرو
  // =========================

  useEffect(() => {
    if (selectedCarId) {
      localStorage.setItem(
        SELECTED_CAR_KEY,
        String(selectedCarId)
      );
    } else {
      localStorage.removeItem(
        SELECTED_CAR_KEY
      );
    }
  }, [selectedCarId]);

  // =========================
  // دریافت یک ماشین
  // =========================

  const getCarById = useCallback(
    async (carId) => {
      if (!carId) {
        throw new Error("شناسه خودرو مشخص نیست");
      }

      try {
        const existingCar = cars.find((car) => String(car.id) === String(carId));
        if (existingCar) return existingCar;

        const response = await getCars();

        console.log(
          "GET CAR BY ID RESPONSE:",
          response
        );

        if (
          response.success &&
          Array.isArray(response.data)
        ) {
          const backendCar =
            response.data.find(
              (car) =>
                String(car.id) ===
                String(carId)
            );

          if (!backendCar) {
            throw new Error(
              "ماشین موردنظر پیدا نشد"
            );
          }

          const transformedCar =
            transformBackendCar(
              backendCar
            );

          setCars((prevCars) => {
            const exists =
              prevCars.some(
                (car) =>
                  String(car.id) ===
                  String(carId)
              );

            if (exists) {
              return prevCars.map(
                (car) =>
                  String(car.id) ===
                  String(carId)
                    ? transformedCar
                    : car
              );
            }

            return [
              ...prevCars,
              transformedCar,
            ];
          });

          return transformedCar;
        }

        throw new Error(
          response.message ||
            "خطا در دریافت اطلاعات ماشین"
        );
      } catch (err) {
        console.error(
          "GET CAR BY ID ERROR:",
          err
        );

        setError(
          extractErrorMessage(
            err,
            "خطا در دریافت اطلاعات ماشین"
          )
        );

        throw err;
      }
    },
    [cars]
  );

  // =========================
  // اضافه کردن ماشین
  // =========================

  const addCar = useCallback(
    async (carData) => {
      setLoading(true);
      setError(null);

      try {
        console.log(
          "CREATE CAR DATA:",
          carData
        );

        const response =
          await createCar(carData);

        console.log(
          "CREATE CAR RESPONSE:",
          response
        );

        if (
          response.success &&
          response.data
        ) {
          const newCar =
            transformBackendCar(
              response.data
            );

          setCars((prevCars) => [
            ...prevCars,
            newCar,
          ]);

          // ماشین جدید به صورت خودکار انتخاب شود
          setSelectedCarId(newCar.id);

          return newCar.id;
        }

        throw new Error(
          response.message ||
            "خطا در ثبت ماشین"
        );
      } catch (err) {
        console.error(
          "CREATE CAR ERROR:",
          err
        );

        setError(
          extractErrorMessage(
            err,
            "خطا در ثبت ماشین"
          )
        );

        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // =========================
  // حذف ماشین
  // =========================

  const deleteCar = useCallback(
    async (carId) => {
      if (!carId) {
        throw new Error(
          "شناسه خودرو مشخص نیست"
        );
      }

      setLoading(true);
      setError(null);

      try {
        console.log(
          "DELETE CAR:",
          carId
        );

        const response =
          await deleteCarApi(carId);

        console.log(
          "DELETE RESPONSE:",
          response
        );

        if (!response.success) {
          throw new Error(
            response.message ||
              "خطا در حذف خودرو"
          );
        }

        setCars((prevCars) =>
          prevCars.filter(
            (car) =>
              String(car.id) !==
              String(carId)
          )
        );

        setSelectedCarId(
          (currentSelectedId) => {
            if (
              String(
                currentSelectedId
              ) !== String(carId)
            ) {
              return currentSelectedId;
            }

            // اگر ماشین انتخاب‌شده حذف شد
            // انتخاب را پاک می‌کنیم
            return null;
          }
        );

        return true;
      } catch (err) {
        console.error(
          "DELETE CAR ERROR:",
          err
        );

        setError(
          extractErrorMessage(
            err,
            "خطا در حذف ماشین"
          )
        );

        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // =========================
  // ویرایش ماشین
  // =========================

  const editCar = useCallback(
    async (carId, carData) => {
      if (!carId) {
        throw new Error(
          "شناسه خودرو مشخص نیست"
        );
      }

      setLoading(true);
      setError(null);

      try {
        console.log(
          "UPDATE CAR ID:",
          carId
        );

        console.log(
          "UPDATE CAR DATA:",
          carData
        );

        const response =
          await updateCar(
            carId,
            carData
          );

        console.log(
          "UPDATE CAR RESPONSE:",
          response
        );

        if (
          response.success &&
          response.data
        ) {
          const updatedCar =
            transformBackendCar(
              response.data
            );

          setCars((prevCars) =>
            prevCars.map(
              (car) =>
                String(car.id) ===
                String(carId)
                  ? updatedCar
                  : car
            )
          );

          setSelectedCarId(carId);

          return updatedCar;
        }

        throw new Error(
          response.message ||
            "خطا در ویرایش خودرو"
        );
      } catch (err) {
        console.error(
          "UPDATE CAR ERROR:",
          err
        );

        setError(
          extractErrorMessage(
            err,
            "خطا در ویرایش خودرو"
          )
        );

        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // =========================
  // تغییر کیلومتر
  // =========================

  const updateMileage = useCallback(
    async (carId, mileage) => {
      try {
        setError(null);

        const response =
          await updateCarMileage(
            carId,
            mileage
          );

        if (!response.success) {
          throw new Error(
            response.message ||
              "خطا در تغییر کیلومتر"
          );
        }

        const updatedMileage =
          Number(
            String(mileage)
              .replace(
                /[۰-۹]/g,
                (d) =>
                  "۰۱۲۳۴۵۶۷۸۹".indexOf(
                    d
                  )
              )
              .replace(
                /[،,٬]/g,
                ""
              )
          );

        setCars((prevCars) =>
          prevCars.map(
            (car) =>
              String(car.id) ===
              String(carId)
                ? {
                    ...car,

                    currentMileage:
                      updatedMileage,

                    Km:
                      updatedMileage.toLocaleString(
                        "fa-IR"
                      ),
                  }
                : car
          )
        );

        return response.data;
      } catch (err) {
        console.error(
          "UPDATE MILEAGE ERROR:",
          err
        );

        setError(
          extractErrorMessage(
            err,
            "خطا در تغییر کیلومتر"
          )
        );

        throw err;
      }
    },
    []
  );

  // =========================
  // انتخاب ماشین
  // =========================

  const selectCar = useCallback(
    (carId) => {
      if (!carId) {
        return;
      }

      // فقط شناسه ماشین ذخیره می‌شود
      setSelectedCarId(carId);
    },
    []
  );

  // A selected car belongs to the current account. Clear both the in-memory
  // list and its persisted id before switching accounts, so the next account
  // never briefly renders the previous user's vehicle.
  const clearCarsForLogout = useCallback(() => {
    setCars([]);
    setSelectedCarId(null);
    setError(null);
    try {
      localStorage.removeItem(SELECTED_CAR_KEY);
    } catch {
      // Storage may be unavailable; the in-memory selection is still reset.
    }
  }, []);

  useEffect(() => {
    window.addEventListener("tasmeh:account-logout", clearCarsForLogout);
    return () => window.removeEventListener("tasmeh:account-logout", clearCarsForLogout);
  }, [clearCarsForLogout]);

  // =========================
  // ماشین انتخاب‌شده
  // =========================

  const getSelectedCar =
    useCallback(() => {
      if (!cars.length) {
        return null;
      }

      const selected =
        cars.find(
          (car) =>
            String(car.id) ===
            String(selectedCarId)
        );

      return selected || cars[0];
    }, [
      cars,
      selectedCarId,
    ]);

  // =========================
  // همه ماشین‌ها
  // =========================

  const getAllCars =
    useCallback(() => {
      return cars;
    }, [cars]);

  // =========================
  // Refresh
  // =========================

  const refreshCars =
    useCallback(() => {
      return fetchCars();
    }, [fetchCars]);

  // =========================
  // Context
  // =========================

  const value = {
    cars,

    selectedCarId,

    loading,

    error,

    addCar,

    getCarById,

    editCar,

    deleteCar,

    updateMileage,

    selectCar,

    clearCarsForLogout,

    getSelectedCar,

    getAllCars,

    refreshCars,
  };

  return (
    <CarContext.Provider value={value}>
      {children}
    </CarContext.Provider>
  );
}

// =========================
// Hook
// =========================

export function useCars() {
  const context =
    useContext(CarContext);

  if (!context) {
    throw new Error(
      "useCars must be used within a CarProvider"
    );
  }

  return context;
}

export default CarContext;
